// ============================================================================
// DashboardV2 TypeScript Types
// ============================================================================

import { Theme } from '@mui/material/styles';

/**
 * Represents a client/customer in the monitoring system.
 */
export interface Client {
  id: number;
  name: string;
  shortName: string;
}

/**
 * Calculated statistics for a client.
 */
export interface ClientStats extends Client {
  total: number;
  up: number;
  down: number;
  warning: number;
  alerts: number;
}

/**
 * Device status values.
 */
export type DeviceStatus = 'up' | 'down' | 'warning';

/**
 * Represents a monitored device (exporter, website, network device).
 * `type` holds the DB category name (e.g. "Infrastructure", "Database", "Network", "Web").
 */
export interface Device {
  id: number;
  name: string;
  displayName: string;
  clientId: number;
  client: string;
  status: DeviceStatus;
  type: string;
  category: string;
  prometheusJob?: string;
  grafanaDashboardUid?: string | null;
  host: string;
  lastSeen: string;
  collectionMethod?: 'push' | 'pull';
  alertCount: number;
  // Website-specific (populated only for website devices)
  url?: string;
  websiteId?: number;
  responseTimeMs?: number | null;
  sslExpiryDays?: number | null;
  sslExpiryDate?: string | null;
  uptimePercent?: number | null;
  lastError?: string | null;
}

/**
 * Global statistics for the dashboard.
 */
export interface GlobalStats {
  totalDevices: number;
  upDevices: number;
  downDevices: number;
  warningDevices: number;
  totalAlerts: number;
}

/**
 * Alert severity levels.
 */
export type AlertSeverity = 'critical' | 'warning' | 'info';

/**
 * Alert status values.
 */
export type AlertStatus = 'active' | 'resolved' | 'acknowledged';

/**
 * Timeline information for an alert.
 */
export interface AlertTimeline {
  created: string;
  createdAgo: string;
  updated: string;
  updatedAgo: string;
  started: string;
  ended: string | null;
}

/**
 * Alert history item for device detail view.
 */
export interface AlertHistoryItem {
  id: number;
  name: string;
  severity: AlertSeverity;
  status: AlertStatus;
  time: string;
  duration: string;
  summary: string;
  description: string;
  timeline: AlertTimeline;
  labels: Record<string, string>;
  annotations: Record<string, string>;
}

/**
 * Filter options for the device table.
 */
export interface DeviceFilters {
  clientId: number | 'all';
  type: string | 'all';
  status: DeviceStatus | 'all';
  search?: string;
}

/**
 * Props for components that need theme-aware styling.
 */
export interface ThemeAwareProps {
  sx?: Record<string, unknown>;
}

/**
 * The active filter applied from clicking a stat card.
 * - 'all' = Total Devices (no status filter)
 * - 'up' / 'down' = Online / Offline filter
 * - 'alerts' = Active Alerts filter
 */
export type StatFilter = 'all' | 'up' | 'down' | 'alerts';

/**
 * Props for the GlobalStatsRow component.
 */
export interface GlobalStatsRowProps extends ThemeAwareProps {
  stats: GlobalStats;
  loading?: boolean;
  activeFilter?: StatFilter;
  onStatClick?: (filter: StatFilter) => void;
}

/**
 * Props for the ClientCard component.
 */
export interface ClientCardProps extends ThemeAwareProps {
  client: ClientStats;
  isSelected: boolean;
  onClick: () => void;
}

/**
 * Props for the ClientCardsRow component.
 */
export interface ClientCardsRowProps extends ThemeAwareProps {
  clients: ClientStats[];
  selectedClientId: number | 'all';
  onClientSelect: (clientId: number | 'all') => void;
  loading?: boolean;
}

/**
 * Props for the DeviceTable component.
 */
export interface DeviceTableProps extends ThemeAwareProps {
  devices: Device[];
  filters: DeviceFilters;
  clients: Client[];
  onFilterChange: (filters: Partial<DeviceFilters>) => void;
  onDeviceClick: (device: Device) => void;
  loading?: boolean;
}

/**
 * Props for the DeviceDetailDrawer component.
 */
export interface DeviceDetailDrawerProps {
  device: Device | null;
  open: boolean;
  onClose: () => void;
  alertHistory?: AlertHistoryItem[];
  onSilenceAlert?: (labels: Record<string, string>) => void;
}

/**
 * Props for the AlertDetailModal component.
 */
export interface AlertDetailModalProps {
  alert: AlertHistoryItem | null;
  open: boolean;
  onClose: () => void;
  onSilence?: (labels: Record<string, string>) => void;
}

/**
 * Props for the GrafanaPreview component.
 */
export interface GrafanaPreviewProps extends ThemeAwareProps {
  url: string;
  deviceName: string;
  height?: number | string;
  showPlaceholder?: boolean;
  onFullscreen?: () => void;
}

// ============================================================================
// Native Metrics Preview Types
// ============================================================================

export type MetricPeriod = '1h' | '6h' | '24h' | '7d';

export type MetricChartType = 'area' | 'stat' | 'stacked-area';

export type MetricUnit = 'percent' | 'bytes' | 'bytes/s' | 'ops/s' | 'seconds' | 'none';

export interface MetricQuery {
  expr: string;
  legend: string;
}

export interface MetricThreshold {
  value: number;
  color: string;
}

export interface MetricPanelDef {
  title: string;
  group: string;
  queries: MetricQuery[];
  chartType: MetricChartType;
  unit: MetricUnit;
  thresholds?: MetricThreshold[];
}

export interface MetricGroupDef {
  group: string;
  panels: MetricPanelDef[];
}

export interface MetricSeriesData {
  time: number;
  [key: string]: number;
}

export interface MetricPanelData {
  def: MetricPanelDef;
  series: MetricSeriesData[];
  legends: string[];
  currentValue?: number;
}

export interface MetricGroupData {
  group: string;
  panels: MetricPanelData[];
}

export interface PrometheusRangeResult {
  metric: Record<string, string>;
  values: [number, string][];
}

export interface PrometheusRangeResponse {
  status: string;
  data: {
    resultType: string;
    result: PrometheusRangeResult[];
  };
}

export interface PrometheusInstantResult {
  metric: Record<string, string>;
  value: [number, string];
}

export interface PrometheusInstantResponse {
  status: string;
  data: {
    resultType: string;
    result: PrometheusInstantResult[];
  };
}

export const METRIC_PERIOD_CONFIG: Record<MetricPeriod, { label: string; seconds: number; step: string }> = {
  '1h': { label: '1 Hour', seconds: 3600, step: '15s' },
  '6h': { label: '6 Hours', seconds: 21600, step: '1m' },
  '24h': { label: '24 Hours', seconds: 86400, step: '5m' },
  '7d': { label: '7 Days', seconds: 604800, step: '30m' },
};

// ============================================================================
// Design System Constants
// ============================================================================

export const getCardBorder = (theme: Theme) => `1px solid ${theme.palette.divider}`;
export const CARD_BORDER_RADIUS = '10px';

/**
 * Health percentage thresholds for color coding.
 */
export const HEALTH_THRESHOLDS = {
  GOOD: 90,      // >= 90% = green
  WARNING: 70,   // >= 70% = yellow/orange
  // < 70% = red
} as const;

/**
 * Maps prometheus_job values to Grafana dashboard UIDs.
 */
export const GRAFANA_DASHBOARD_MAP: Record<string, string> = {
  node_exporter: 'cansense-node-exporter',
  windows_exporter: 'cansense-windows-exporter',
  process_exporter: 'cansense-process-exporter',
  blackbox: 'cansense-blackbox-exporter',
  sql_exporter: 'cansense-sql-exporter',
  vmware_esxi: 'cansense-vmware-esxi',
  prometheus: 'cansense-prometheus',
  alertmanager: 'cansense-alertmanager',
  'cansds-log-exporter': 'cansds-logs',
  'cansense-push': 'cansense-node-exporter',
  website_monitor: 'website-monitoring',
};
